// src/pages/HomePage.js
/**
 * Home dashboard shown **after a successful sign-in**.
 *
 * Purpose ──────────────────────────────────────────────────────────
 *  • Shows the scan modes currently permitted for the user's study.
 *  • Sends each scan through the metadata review gate before recording.
 *
 *  • Keeps the UI deliberately sparse so it loads instantly even on
 *    slow networks (no heavy dependencies, no remote calls).
 *
 * UX / visual layout ───────────────────────────────────────────────
 *  • Small mode tiles keep the home page aligned with level selection.
 *
 * @returns {JSX.Element}
 */

import React, { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import Spinner from "../components/Spinner";
import RecordingModeTiles from "../components/recording/RecordingModeTiles";
import { Link } from "react-router-dom";
import { APP_VERSION } from "../utils/version";
/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function HomePage({
  firstRunByVariant,
  isPermitted,
  hasQuestionnaires,
  loadingModes,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    trackEvent("home_view");
  }, []);

  const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end items-center gap-4">
      <div className="h-full w-full flex flex-col justify-start items-center">
        <img
          src={DEMO_MODE ? "/pdx_demo_logo.png" : "/pdx_logo.png"}
          alt=""
          loading="eager"
          className="max-h-24 w-full object-contain"
        />
        {!DEMO_MODE && <p className="">{t("labelling.investigationOnly")}</p>}
        <p>
          {t("labelling.version")} {APP_VERSION}
        </p>
      </div>

      <section className="w-full max-w-lg h-full flex flex-col justify-end items-center gap-4">
        {loadingModes ? (
          <div className="w-full min-h-52">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col w-full gap-4">
            <RecordingModeTiles
              firstRunByVariant={firstRunByVariant}
              isPermitted={isPermitted}
            />
          </div>
        )}
        {/* bottom-row buttons */}
        {hasQuestionnaires ? (
          <Link
            to="/questionnaires"
            className="outlined-button w-full"
            onClick={() => trackEvent("home_click_questionnaires")}
          >
            <p className="subtitle-text"> {t("home.questionnaires")}</p>
          </Link>
        ) : null}
      </section>
    </div>
  );
}

export default memo(HomePage);
